import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";

import { CATEGORY_NAMES } from "constants/dispensary";
import { Action, Category } from "constants/events";
import { Brand } from "custom-types/Brand";
import { MappedFilterOption } from "custom-types/Shop";
import { Strain } from "custom-types/Strain";
import { useEventTracker } from "hooks/useEventTracker";
import { isCategoryHub } from "hooks/useShop";

import CoinList from "components/CoinList";
import Image from "components/Image";
import LeaflyNug from "components/LeaflyNug";
import Placeholder, { Rect } from "components/Placeholder";

const brandPlaceholder =
  "https://public.leafly.com/consumer-web/brands/brand-placeholder.svg";

const ShopSelection: React.FC<{
  className?: string;
  filters: MappedFilterOption[];
  selection?: Brand | Strain;
  loading?: boolean;
}> = ({ className, filters, selection, loading }) => {
  const { publishEvent } = useEventTracker();

  const {
    query,
    query: { shopCategory, product_category },
  } = useRouter();

  const categoryHub = isCategoryHub(query);

  const categoryOptions =
    filters.find((filter) => filter.name === "product_category")?.values ?? [];
  const sortedCategories = categoryOptions
    ? [
        String(product_category),
        ...Object.keys(CATEGORY_NAMES).filter(
          (category) =>
            category !== product_category &&
            categoryOptions?.includes(category),
        ),
      ]
    : [];

  return (
    <div className={className}>
      {shopCategory === "strain" ? (
        loading ? (
          <div className="flex">
            <Placeholder width="75" height="75" className="mb-sm">
              <Rect width="100%" height="100%" />
            </Placeholder>
            <div className="flex flex-col ml-4">
              <div className="grid grid-cols-3 gap-2 mb-md">
                {new Array(3).fill(null).map((_, i) => (
                  <div
                    className="bg-leafly-white h-7"
                    key={`phenotype-placeholder-${i}`}
                  />
                ))}
              </div>
              <Placeholder width="200" height="36" className="rounded-full">
                <Rect width="200" height="36" />
              </Placeholder>
            </div>
          </div>
        ) : selection ? (
          <div className="flex" data-testid="selection-strain">
            <LeaflyNug strain={selection as Strain} />
            <div className="flex flex-col ml-2">
              <div className="flex font-bold text-xs mb-md">
                {!!(selection as Strain).phenotype && (
                  <div
                    className="px-sm py-xs mr-sm rounded bg-leafly-white"
                    data-testid="menu-item__cannabinoids--thc"
                  >
                    {(selection as Strain).phenotype}
                  </div>
                )}
                <div
                  className="px-sm py-xs mr-sm rounded bg-deep-green-20"
                  data-testid="menu-item__cannabinoids--thc"
                >
                  {`THC ${
                    (selection as Strain).cannabinoids?.thc?.percentile50 ||
                    (selection as Strain).cannabinoids?.thc?.percentile50 === 0
                      ? `${
                          (selection as Strain).cannabinoids?.thc?.percentile50
                        }%`
                      : "—"
                  }`}
                </div>
                <div
                  className="px-sm py-xs rounded bg-deep-green-20"
                  data-testid="menu-item__cannabinoids--thc"
                >
                  {`CBD ${
                    (selection as Strain).cannabinoids?.cbd?.percentile50 ||
                    (selection as Strain).cannabinoids?.cbd?.percentile50 === 0
                      ? `${
                          (selection as Strain).cannabinoids?.cbd?.percentile50
                        }%`
                      : "—"
                  }`}
                </div>
              </div>
              <Link
                href={`/strains/${selection.slug}`}
                className="button"
                data-testid="strain-details-link"
                onClick={() => {
                  publishEvent({
                    action: Action.click,
                    category: Category.searchShopResults,
                    label: "see strain details",
                    strainId: selection.id,
                  });
                }}
              >
                see strain details
              </Link>
            </div>
          </div>
        ) : (
          <></>
        )
      ) : shopCategory === "brand" ? (
        loading ? (
          <div className="flex">
            <Placeholder width="75" height="75" className="mr-md flex-shrink-0">
              <Rect width="100%" height="100%" />
            </Placeholder>
            <Placeholder
              width="100%"
              height="36"
              className="rounded-full max-w-[322px]"
            >
              <Rect width="100%" height="36" />
            </Placeholder>
          </div>
        ) : selection ? (
          <div className="flex items-start" data-testid="selection-brand">
            <Image
              src={(selection as Brand).logo || brandPlaceholder}
              alt={(selection as Brand).name}
              sizes={[160]}
              className="w-[80px] h-[80px] rounded-full mr-md flex-shrink-0"
            />
            <Link
              href={`/brands/${(selection as Brand).slug}`}
              className="button"
              data-testid="brand-details-link"
              onClick={() => {
                publishEvent({
                  action: Action.click,
                  brandId: (selection as Brand).id,
                  category: Category.searchShopResults,
                  label: "see brand details",
                });
              }}
            >
              see brand details
            </Link>
          </div>
        ) : (
          <></>
        )
      ) : categoryHub && sortedCategories ? (
        <CoinList coins={sortedCategories} />
      ) : (
        <></>
      )}
    </div>
  );
};

export default ShopSelection;
